import React, { useState, useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { X, MapPin, Satellite, Map as MapIcon, Check } from 'lucide-react';
import 'mapbox-gl/dist/mapbox-gl.css';

// Set Mapbox access token
const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || 'pk.eyJ1IjoiY2xpZmZpc29uIiwiYSI6ImNtNHZqZ3p1cTA3MmcycHE2bDdtdmlwdmYifQ.6kIxcCZXH6I9gHe41FhkMw';
console.log('Mapbox token available:', MAPBOX_TOKEN ? 'Yes' : 'No');
mapboxgl.accessToken = MAPBOX_TOKEN;

interface FullScreenMapPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onLocationSelect: (lat: number, lng: number, radius: number) => void;
  initialLat?: number;
  initialLng?: number;
  address?: string;
  geofenceType?: 'circle';
  geofenceRadius?: number;
}

export const FullScreenMapPicker: React.FC<FullScreenMapPickerProps> = ({
  isOpen,
  onClose,
  onLocationSelect,
  initialLat = 26.1420,
  initialLng = -81.7948,
  address = '',
  geofenceRadius = 200
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const marker = useRef<mapboxgl.Marker | null>(null);
  const geofenceCircle = useRef<string | null>(null);

  const [selectedLat, setSelectedLat] = useState(initialLat);
  const [selectedLng, setSelectedLng] = useState(initialLng);
  const [mapStyle, setMapStyle] = useState<'streets' | 'satellite'>('satellite');
  const [currentRadius, setCurrentRadius] = useState(geofenceRadius);
  const [geocoding, setGeocoding] = useState(false);

  // Update state when props change (for editing different jobs)
  useEffect(() => {
    if (isOpen) {
      console.log('Map picker opened with:', { initialLat, initialLng, geofenceRadius });
      setSelectedLat(initialLat);
      setSelectedLng(initialLng);
      setCurrentRadius(geofenceRadius);
    }
  }, [isOpen, initialLat, initialLng, geofenceRadius]);

  // Geocode address when map opens
  useEffect(() => {
    const trimmedAddress = address.trim();
    if (!isOpen || !trimmedAddress || geocoding) return;

    const geocodeAddress = async () => {
      setGeocoding(true);
      try {
        const encodedAddress = encodeURIComponent(trimmedAddress);
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodedAddress}.json?access_token=${MAPBOX_TOKEN}&country=US&limit=1`
        );

        if (response.ok) {
          const data = await response.json();
          if (data.features && data.features.length > 0) {
            const [lng, lat] = data.features[0].center;
            console.log('Geocoded address to:', { lat, lng });
            setSelectedLat(lat);
            setSelectedLng(lng);

            // Wait for map to be ready, then update
            const updateMapWhenReady = () => {
              if (map.current && map.current.loaded()) {
                map.current.flyTo({ center: [lng, lat], zoom: 17 });
                if (marker.current) {
                  marker.current.setLngLat([lng, lat]);
                }
                addGeofenceCircle(lat, lng, currentRadius);
              } else if (map.current) {
                // Map exists but not loaded yet, wait for load event
                map.current.once('load', () => {
                  map.current!.flyTo({ center: [lng, lat], zoom: 17 });
                  if (marker.current) {
                    marker.current.setLngLat([lng, lat]);
                  }
                  addGeofenceCircle(lat, lng, currentRadius);
                });
              } else {
                // Map doesn't exist yet, try again after a brief delay
                setTimeout(updateMapWhenReady, 100);
              }
            };

            updateMapWhenReady();
          }
        }
      } catch (error) {
        console.error('Geocoding error:', error);
      } finally {
        setGeocoding(false);
      }
    };

    // Slight delay to ensure map initialization has started
    const timer = setTimeout(geocodeAddress, 100);
    return () => clearTimeout(timer);
  }, [isOpen, address]);

  // Initialize map when component opens
  useEffect(() => {
    if (!isOpen || !mapContainer.current || map.current) return;

    console.log('Initializing map at:', { lat: initialLat, lng: initialLng });

    try {
      // Initialize map
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: mapStyle === 'streets'
          ? 'mapbox://styles/mapbox/streets-v12'
          : 'mapbox://styles/mapbox/satellite-streets-v12',
        center: [initialLng, initialLat],
        zoom: 15,
        maxZoom: 22,
        attributionControl: false
      });

      map.current.on('error', (e) => {
        console.error('Mapbox error:', e);
      });
    } catch (error) {
      console.error('Failed to initialize map:', error);
      return;
    }

    // Add navigation controls
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Create draggable marker
    const el = document.createElement('div');
    el.className = 'custom-marker';
    const svgIcon = '<svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg"><path d="M16 2C11.589 2 8 5.589 8 10c0 6 8 20 8 20s8-14 8-20c0-4.411-3.589-8-8-8z" fill="#dc2626"/><circle cx="16" cy="10" r="4" fill="white"/></svg>';
    el.style.backgroundImage = 'url(data:image/svg+xml;base64,' + btoa(svgIcon) + ')';
    el.style.width = '32px';
    el.style.height = '32px';
    el.style.cursor = 'grab';

    marker.current = new mapboxgl.Marker({
      element: el,
      draggable: true
    })
      .setLngLat([initialLng, initialLat])
      .addTo(map.current);

    // Update coordinates when marker is dragged
    marker.current.on('dragend', () => {
      if (marker.current) {
        const lngLat = marker.current.getLngLat();
        setSelectedLat(lngLat.lat);
        setSelectedLng(lngLat.lng);
        addGeofenceCircle(lngLat.lat, lngLat.lng, currentRadius);
      }
    });

    // Click on map to move marker
    map.current.on('click', (e) => {
      const { lng, lat } = e.lngLat;
      setSelectedLat(lat);
      setSelectedLng(lng);
      if (marker.current) {
        marker.current.setLngLat([lng, lat]);
      }
      addGeofenceCircle(lat, lng, currentRadius);
    });

    // Add geofence circle after map loads (only if not geocoding an address)
    map.current.on('load', () => {
      // If there's an address, geocoding will handle the circle
      // Otherwise, draw circle at initial/selected coordinates
      const trimmedAddress = address.trim();
      if (!trimmedAddress) {
        addGeofenceCircle(initialLat, initialLng, geofenceRadius);
      }
    });

    // Cleanup on unmount
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
        marker.current = null;
      }
    };
  }, [isOpen]);

  // Update map style when changed
  useEffect(() => {
    if (map.current) {
      const styleUrl = mapStyle === 'streets'
        ? 'mapbox://styles/mapbox/streets-v12'
        : 'mapbox://styles/mapbox/satellite-streets-v12';

      map.current.setStyle(styleUrl);

      // Re-add geofence circle after style loads
      map.current.once('style.load', () => {
        addGeofenceCircle(selectedLat, selectedLng, currentRadius);
      });
    }
  }, [mapStyle]);

  // Add geofence circle
  const addGeofenceCircle = (lat: number, lng: number, radius: number) => {
    if (!map.current || !map.current.isStyleLoaded()) return;

    // Remove existing geofence
    if (geofenceCircle.current) {
      try {
        if (map.current.getSource(geofenceCircle.current)) {
          map.current.removeLayer(geofenceCircle.current + '-fill');
          map.current.removeLayer(geofenceCircle.current);
          map.current.removeSource(geofenceCircle.current);
        }
      } catch (e) {
        // Layer might not exist
      }
    }

    const sourceId = 'geofence-circle-' + Date.now();
    geofenceCircle.current = sourceId;

    // Create circle using Turf.js circle function
    const steps = 64;
    const km = radius / 1000;
    const coordinates: [number, number][] = [];

    for (let i = 0; i < steps; i++) {
      const angle = (i / steps) * 360;
      const rad = (angle * Math.PI) / 180;

      // Calculate point on circle
      const dx = km * Math.cos(rad);
      const dy = km * Math.sin(rad);

      // Convert km to degrees (approximate)
      const deltaLat = dy / 110.574;
      const deltaLng = dx / (111.320 * Math.cos((lat * Math.PI) / 180));

      coordinates.push([lng + deltaLng, lat + deltaLat]);
    }

    // Close the circle
    coordinates.push(coordinates[0]);

    // Add source for circle
    map.current.addSource(sourceId, {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [coordinates]
        },
        properties: {}
      }
    });

    // Add fill layer
    map.current.addLayer({
      id: sourceId + '-fill',
      type: 'fill',
      source: sourceId,
      paint: {
        'fill-color': '#3b82f6',
        'fill-opacity': 0.15
      }
    });

    // Add border layer
    map.current.addLayer({
      id: sourceId,
      type: 'line',
      source: sourceId,
      paint: {
        'line-color': '#3b82f6',
        'line-width': 2
      }
    });
  };

  const handleRadiusChange = (newRadius: number) => {
    setCurrentRadius(newRadius);
    addGeofenceCircle(selectedLat, selectedLng, newRadius);
  };

  const handleConfirm = () => {
    onLocationSelect(selectedLat, selectedLng, currentRadius);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay}>
      {/* Map Container */}
      <div ref={mapContainer} style={styles.mapContainer} />

      {/* Geocoding Loading Overlay */}
      {geocoding && (
        <div style={styles.loadingOverlay}>
          <div style={styles.loadingSpinner}>
            <MapPin size={32} style={{ animation: 'pulse 1.5s ease-in-out infinite' }} />
            <div style={styles.loadingText}>Finding address on map...</div>
          </div>
        </div>
      )}

      {/* Top Controls */}
      <div style={styles.topBar}>
        <button onClick={onClose} style={styles.closeButton}>
          <X size={20} />
        </button>

        <div style={styles.addressDisplay}>
          <MapPin size={16} />
          <span style={styles.addressText}>{address || 'Select Location'}</span>
        </div>

        <div style={styles.styleToggle}>
          <button
            onClick={() => setMapStyle('streets')}
            style={{
              ...styles.styleButton,
              ...(mapStyle === 'streets' ? styles.styleButtonActive : {})
            }}
          >
            <MapIcon size={16} />
          </button>
          <button
            onClick={() => setMapStyle('satellite')}
            style={{
              ...styles.styleButton,
              ...(mapStyle === 'satellite' ? styles.styleButtonActive : {})
            }}
          >
            <Satellite size={16} />
          </button>
        </div>
      </div>

      {/* Bottom Controls */}
      <div style={styles.bottomBar}>
        {/* Coordinates Display */}
        <div style={styles.coordinatesDisplay}>
          <div style={styles.coordLabel}>Latitude:</div>
          <div style={styles.coordValue}>{selectedLat.toFixed(6)}</div>
          <div style={styles.coordLabel}>Longitude:</div>
          <div style={styles.coordValue}>{selectedLng.toFixed(6)}</div>
        </div>

        {/* Radius Slider */}
        <div style={styles.radiusControl}>
          <label style={styles.radiusLabel}>
            Geofence Radius: {currentRadius}m ({Math.round(currentRadius * 3.28084)}ft)
          </label>
          <input
            type="range"
            min="50"
            max="1000"
            step="10"
            value={currentRadius}
            onChange={(e) => handleRadiusChange(parseInt(e.target.value))}
            style={styles.rangeInput}
          />
        </div>

        {/* Confirm Button */}
        <button onClick={handleConfirm} style={styles.confirmButton}>
          <Check size={20} />
          Confirm Location
        </button>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    backgroundColor: '#000'
  },
  mapContainer: {
    width: '100%',
    height: '100%'
  },
  topBar: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    backdropFilter: 'blur(10px)',
    zIndex: 10
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: 'white',
    cursor: 'pointer',
    padding: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  addressDisplay: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: 'white',
    fontSize: '14px',
    overflow: 'hidden'
  },
  addressText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const
  },
  styleToggle: {
    display: 'flex',
    gap: '8px'
  },
  styleButton: {
    background: 'rgba(255, 255, 255, 0.1)',
    border: 'none',
    color: 'white',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  styleButtonActive: {
    background: '#3b82f6'
  },
  bottomBar: {
    position: 'absolute' as const,
    bottom: 0,
    left: 0,
    right: 0,
    padding: '16px',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    backdropFilter: 'blur(10px)',
    zIndex: 10,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px'
  },
  coordinatesDisplay: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto 1fr',
    gap: '8px',
    alignItems: 'center',
    fontSize: '14px'
  },
  coordLabel: {
    color: '#9ca3af',
    fontSize: '12px'
  },
  coordValue: {
    color: 'white',
    fontFamily: 'monospace'
  },
  radiusControl: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px'
  },
  radiusLabel: {
    color: 'white',
    fontSize: '14px',
    fontWeight: '600' as const
  },
  rangeInput: {
    width: '100%',
    height: '6px',
    borderRadius: '3px',
    outline: 'none',
    background: 'rgba(255, 255, 255, 0.2)'
  },
  confirmButton: {
    padding: '12px 24px',
    backgroundColor: '#22c55e',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600' as const,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  },
  loadingOverlay: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000
  },
  loadingSpinner: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '16px',
    color: 'white'
  },
  loadingText: {
    fontSize: '16px',
    fontWeight: '500' as const
  }
};
